import React, { useState, useEffect, useCallback } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Switch } from '../../components/ui/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '../../components/ui/card';
import {
  Plus, Edit, Trash2, Newspaper, Sparkles, Star, EyeOff, Eye,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import AdminSidebar from '../../components/admin/AdminSidebar';

const EMPTY_FORM = {
  title: '',
  summary: '',
  content: '',
  category: 'news',
  cover_url: '',
  is_featured: false,
  is_published: true,
  display_order: 0,
};

export default function NewsPage() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [uploadingCover, setUploadingCover] = useState(false);
  const [coverPreview, setCoverPreview] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  const fetchPosts = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/news/admin/all`, {
        withCredentials: true,
      });
      setPosts(data);
    } catch (error) {
      toast.error('Erro ao carregar posts');
    } finally {
      setLoading(false);
    }
  }, [API_URL]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const handleCoverUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Selecione uma imagem válida');
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error('Imagem muito grande. Máximo 8MB');
      return;
    }
    const reader = new FileReader();
    reader.onloadend = () => setCoverPreview(reader.result);
    reader.readAsDataURL(file);

    setUploadingCover(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await axios.post(`${API_URL}/api/news/upload-cover`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setFormData((prev) => ({ ...prev, cover_url: data.cover_url }));
      toast.success('Imagem carregada');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro no upload');
      setCoverPreview(null);
    } finally {
      setUploadingCover(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.summary.trim() || !formData.content.trim()) {
      toast.error('Preencha título, resumo e conteúdo');
      return;
    }
    try {
      if (editing) {
        await axios.put(`${API_URL}/api/news/${editing.id}`, formData, { withCredentials: true });
        toast.success('Post atualizado');
      } else {
        await axios.post(`${API_URL}/api/news/`, formData, { withCredentials: true });
        toast.success('Post criado');
      }
      setDialogOpen(false);
      resetForm();
      fetchPosts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar');
    }
  };

  const handleEdit = (post) => {
    setEditing(post);
    setFormData({
      title: post.title,
      summary: post.summary,
      content: post.content,
      category: post.category,
      cover_url: post.cover_url || '',
      is_featured: post.is_featured,
      is_published: post.is_published,
      display_order: post.display_order || 0,
    });
    setCoverPreview(post.cover_url ? (post.cover_url.startsWith('http') ? post.cover_url : `${API_URL}${post.cover_url}`) : null);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API_URL}/api/news/${id}`, { withCredentials: true });
      toast.success('Post removido');
      fetchPosts();
    } catch (error) {
      toast.error('Erro ao remover');
    } finally {
      setDeleteConfirmId(null);
    }
  };

  const resetForm = () => {
    setEditing(null);
    setFormData(EMPTY_FORM);
    setCoverPreview(null);
  };

  return (
    <AdminSidebar
      title="Notícias e Projetos"
      subtitle="Crie posts e destaque os principais no popup inicial"
    >
        <Card>
          <CardHeader>
            <div className="flex flex-wrap justify-between items-center gap-3">
              <div>
                <CardTitle>Posts</CardTitle>
                <CardDescription>
                  Crie notícias e projetos. Marque como destaque para aparecer no popup inicial.
                </CardDescription>
              </div>
              <Dialog open={dialogOpen} onOpenChange={(open) => {
                setDialogOpen(open);
                if (!open) resetForm();
              }}>
                <DialogTrigger asChild>
                  <Button data-testid="new-post-button">
                    <Plus className="mr-2 h-4 w-4" />
                    Novo Post
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>{editing ? 'Editar Post' : 'Novo Post'}</DialogTitle>
                    <DialogDescription>
                      O conteúdo aceita Markdown: **negrito**, *itálico*, [link](https://...), - listas
                    </DialogDescription>
                  </DialogHeader>
                  <form onSubmit={handleSubmit} className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2 md:col-span-2">
                        <Label htmlFor="title">Título *</Label>
                        <Input
                          id="title"
                          data-testid="post-title-input"
                          value={formData.title}
                          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                          required
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="category">Categoria</Label>
                        <select
                          id="category"
                          data-testid="post-category-select"
                          value={formData.category}
                          onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                          className="w-full h-10 px-3 rounded-md border border-input bg-white text-sm"
                        >
                          <option value="news">Notícia</option>
                          <option value="project">Projeto</option>
                        </select>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="summary">Resumo (1-2 linhas) *</Label>
                      <Textarea
                        id="summary"
                        data-testid="post-summary-input"
                        rows={2}
                        value={formData.summary}
                        onChange={(e) => setFormData({ ...formData, summary: e.target.value })}
                        required
                      />
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="content">Conteúdo (Markdown) *</Label>
                      <Textarea
                        id="content"
                        data-testid="post-content-input"
                        rows={8}
                        placeholder={"Escreva o conteúdo aqui...\n\n**Exemplo:** texto em negrito, *itálico*, [link](https://exemplo.com)\n\n- item 1\n- item 2"}
                        value={formData.content}
                        onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                        required
                        className="font-mono text-sm"
                      />
                    </div>

                    <div className="space-y-2">
                      <Label>Imagem de Capa</Label>
                      <div className="flex flex-col gap-3">
                        {coverPreview && (
                          <div className="border rounded-lg p-2 bg-gray-50">
                            <img src={coverPreview} alt="Preview" className="max-h-48 w-full object-cover rounded" />
                          </div>
                        )}
                        <div className="flex gap-2">
                          <Input
                            type="file"
                            accept="image/*"
                            onChange={handleCoverUpload}
                            disabled={uploadingCover}
                            data-testid="post-cover-input"
                          />
                          {uploadingCover && (
                            <div className="flex items-center px-3">
                              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-blue-600"></div>
                            </div>
                          )}
                        </div>
                        <p className="text-xs text-gray-500">JPG, PNG ou WEBP até 8MB</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="display_order">Ordem</Label>
                        <Input
                          id="display_order"
                          type="number"
                          value={formData.display_order}
                          onChange={(e) => setFormData({ ...formData, display_order: parseInt(e.target.value) || 0 })}
                        />
                      </div>
                      <div className="flex items-center gap-3 pt-6">
                        <Switch
                          id="is_published"
                          checked={formData.is_published}
                          onCheckedChange={(v) => setFormData({ ...formData, is_published: v })}
                          data-testid="post-published-switch"
                        />
                        <Label htmlFor="is_published" className="cursor-pointer">
                          Publicado
                        </Label>
                      </div>
                      <div className="flex items-center gap-3 pt-6">
                        <Switch
                          id="is_featured"
                          checked={formData.is_featured}
                          onCheckedChange={(v) => setFormData({ ...formData, is_featured: v })}
                          data-testid="post-featured-switch"
                        />
                        <Label htmlFor="is_featured" className="cursor-pointer">
                          Destaque no popup
                        </Label>
                      </div>
                    </div>

                    <div className="flex justify-end gap-2 pt-2">
                      <Button type="button" variant="outline" onClick={() => { setDialogOpen(false); resetForm(); }}>
                        Cancelar
                      </Button>
                      <Button type="submit" data-testid="post-save-button">
                        {editing ? 'Atualizar' : 'Criar'}
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
              </div>
            ) : posts.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                Nenhum post cadastrado. Clique em &quot;Novo Post&quot; para começar.
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Título</TableHead>
                    <TableHead>Categoria</TableHead>
                    <TableHead className="text-center">Status</TableHead>
                    <TableHead className="text-center">Destaque</TableHead>
                    <TableHead className="text-center">Ordem</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {posts.map((post) => (
                    <TableRow key={post.id} data-testid={`post-row-${post.id}`}>
                      <TableCell className="font-medium max-w-md">
                        <div className="truncate">{post.title}</div>
                        <div className="text-xs text-gray-500 truncate">{post.summary}</div>
                      </TableCell>
                      <TableCell>
                        {post.category === 'project' ? (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-amber-100 text-amber-800 text-xs font-semibold">
                            <Sparkles size={10} /> Projeto
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-teal-100 text-teal-800 text-xs font-semibold">
                            <Newspaper size={10} /> Notícia
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {post.is_published ? (
                          <span className="inline-flex items-center gap-1 text-emerald-600 text-xs font-semibold">
                            <Eye size={12} /> Publicado
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-gray-500 text-xs">
                            <EyeOff size={12} /> Rascunho
                          </span>
                        )}
                      </TableCell>
                      <TableCell className="text-center">
                        {post.is_featured ? (
                          <Star size={16} className="inline text-amber-500 fill-amber-500" />
                        ) : (
                          <Star size={16} className="inline text-gray-300" />
                        )}
                      </TableCell>
                      <TableCell className="text-center">{post.display_order}</TableCell>
                      <TableCell className="text-right space-x-1">
                        <Button variant="ghost" size="sm" onClick={() => handleEdit(post)} data-testid={`edit-${post.id}`}>
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setDeleteConfirmId(post.id)} data-testid={`delete-${post.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

      <AlertDialog open={!!deleteConfirmId} onOpenChange={() => setDeleteConfirmId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmar exclusão</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja remover este post? Esta ação é permanente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDelete(deleteConfirmId)}>
              Confirmar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AdminSidebar>
  );
}
